/**
 * Runtime config for the institutional SPA. The country registry, the per-market content, the edge `geoCountry`
 * hint and the global fallback market are all supplied at runtime — never hardcoded here, so the site stays
 * neutral (ADR-0013). The platform serves `/site-config.json` (same-origin, generated at deploy from the served
 * country packs); a missing or unreachable config degrades to the neutral "choose your market" bootstrap.
 */
import { EMPTY_REGISTRY, type SiteRegistry } from '../src/registry.js';
import type { SiteEntryInputs } from '../src/entry.js';
import type { SiteContent } from '@vello/web-entry';
import type { SiteWidgetConfig } from '../src/widget-embed.js';

export interface SiteRuntimeConfig {
  readonly registry: SiteRegistry;
  /** The edge country header value (`cf-ipcountry`), injected server-side — a hint only. */
  readonly geoCountryHeader?: string | null;
  /**
   * A same-origin-preferred URL that returns the visitor's country for real IP-geo, when the edge cannot inject
   * `geoCountryHeader` (the Cloudflare proxy is off — Traefik owns TLS — so `cf-ipcountry` is absent). The
   * response is read fail-soft: any 2-letter `country` / `country_code` field is used; an empty URL, a slow or
   * failed call, or an unparsable body all degrade to the fallback market (no regression). Swap this for a
   * first-party `/geo` endpoint (nginx `geoip2`) when a GeoIP DB is provisioned — no code change needed.
   */
  readonly geoLookupUrl?: string | null;
  /** The platform's global fallback market (neutral, injected). Empty when none is configured yet. */
  readonly fallbackCountry: string;
  /** Per-market institutional content, keyed by ISO 3166-1 alpha-2 (upper-case). A served market with no entry
   *  renders as "available, generic" is avoided — the site shows content only for markets that supply it. */
  readonly content?: Readonly<Record<string, SiteContent>>;
  /**
   * The Central widget on the site (D427): the Vello-site embed's public key (+ optionally the API origin). Absent → no
   * widget. Written by `scripts/gen-site-config.mjs` from `VELLO_SITE_WIDGET_KEY` / `VELLO_SITE_WIDGET_API`.
   */
  readonly widget?: SiteWidgetConfig;
  /**
   * The Vello api origin the public pages call: the account-deletion page (X-03, D439) — `POST /v1/auth/otp/request` and
   * `POST /v1/privacy/public/deletion-requests` — and the trip-share link and legal texts (D440) — `GET
   * /v1/public/trip-shares/:token`, `GET /v1/public/legal-documents/…`. Written by `scripts/gen-site-config.mjs` from
   * `VELLO_SITE_API_ORIGIN`. Optional runtime override; absent → the build's
   * `VITE_VELLO_API_BASE_URL`, else {@link DEFAULT_API_BASE_URL}. See {@link resolveApiBaseUrl}.
   */
  readonly apiBaseUrl?: string;
  /**
   * Optional per-market country calling code (ISO 3166-1 alpha-2 → digits, e.g. from the pack's `phone.callingCode`),
   * used only to PREFILL the deletion page's phone field with `+<digits>`. Absent → the visitor types the full
   * international number (the page never assumes a country).
   */
  readonly phoneCallingCodes?: Readonly<Record<string, string>>;
}

/** The production api origin — the default when neither the runtime config nor the build names one. */
export const DEFAULT_API_BASE_URL = 'https://api.usevello.app';

const API_ORIGIN_SHAPE = /^https:\/\/[^/\s?#]+$|^http:\/\/(?:localhost|127\.0\.0\.1)(?::\d+)?$/;

/**
 * The api origin for the public pages, most-authoritative first: the runtime config's `apiBaseUrl` (written at deploy
 * into `/site-config.json`), then the build-time Vite env `VITE_VELLO_API_BASE_URL` (e.g. a staging build), then
 * {@link DEFAULT_API_BASE_URL}. Only a bare https origin (http on loopback, for local dev) is accepted; anything else
 * falls through to the next source. The api must answer CORS for the site's origin on the two routes the page calls.
 */
export function resolveApiBaseUrl(cfg: SiteRuntimeConfig, buildEnv: string | undefined = import.meta.env.VITE_VELLO_API_BASE_URL): string {
  for (const candidate of [cfg.apiBaseUrl, buildEnv]) {
    const origin = typeof candidate === 'string' ? candidate.trim().replace(/\/+$/, '') : '';
    if (API_ORIGIN_SHAPE.test(origin)) return origin;
  }
  return DEFAULT_API_BASE_URL;
}

/** The `+<calling code>` to prefill the phone field for a market, or '' when the config names none (neutral). */
export function phonePrefixFor(cfg: SiteRuntimeConfig, country: string): string {
  const digits = cfg.phoneCallingCodes?.[country.toUpperCase()];
  return typeof digits === 'string' && /^[1-9]\d{0,3}$/.test(digits) ? `+${digits}` : '';
}

declare global {
  interface Window {
    __VELLO_SITE_CONFIG__?: SiteRuntimeConfig;
  }
}

const KEY_COUNTRY = 'vello.site.country';
const KEY_LOCALE = 'vello.site.locale';

/** The neutral bootstrap config (empty registry, no fallback, no content). */
const NEUTRAL_CONFIG: SiteRuntimeConfig = { registry: EMPTY_REGISTRY, fallbackCountry: '', content: {} };

/**
 * Load the runtime config, most-authoritative first: an edge-injected `window.__VELLO_SITE_CONFIG__` wins;
 * otherwise fetch the same-origin `/site-config.json` (the deploy generates it from the served country packs);
 * otherwise the neutral bootstrap. Fail-soft — any error degrades to neutral, never throws.
 */
export async function loadRuntimeConfig(): Promise<SiteRuntimeConfig> {
  if (typeof window !== 'undefined' && window.__VELLO_SITE_CONFIG__ !== undefined) return window.__VELLO_SITE_CONFIG__;
  try {
    const res = await fetch('/site-config.json', { credentials: 'omit', cache: 'no-cache' });
    if (!res.ok) return NEUTRAL_CONFIG;
    const cfg = (await res.json()) as SiteRuntimeConfig;
    return { registry: cfg.registry ?? EMPTY_REGISTRY, fallbackCountry: cfg.fallbackCountry ?? '', geoCountryHeader: cfg.geoCountryHeader ?? null, geoLookupUrl: cfg.geoLookupUrl ?? null, content: cfg.content ?? {}, ...(cfg.widget !== undefined ? { widget: cfg.widget } : {}), ...(typeof cfg.apiBaseUrl === 'string' ? { apiBaseUrl: cfg.apiBaseUrl } : {}), ...(cfg.phoneCallingCodes !== undefined ? { phoneCallingCodes: cfg.phoneCallingCodes } : {}) };
  } catch {
    // config unreachable (offline / not deployed) — the neutral bootstrap renders "choose a market"
    return NEUTRAL_CONFIG;
  }
}

/** Extract a plausible ISO 3166-1 alpha-2 country from a geo-lookup JSON body (fail-soft). */
function pickCountry(body: unknown): string | null {
  if (typeof body !== 'object' || body === null) return null;
  const rec = body as Record<string, unknown>;
  const raw = rec['country'] ?? rec['country_code'] ?? rec['countryCode'];
  if (typeof raw !== 'string') return null;
  const cc = raw.trim().toUpperCase();
  return /^[A-Z]{2}$/.test(cc) ? cc : null;
}

/**
 * Resolve the visitor's real country via the configured `geoLookupUrl` (real IP-geo). Fail-soft: an unset URL,
 * a timeout ({@link timeoutMs}), a non-OK response, or an unparsable body all return `null` so the caller keeps
 * the fallback market — the redirect never blocks on this and never regresses. Returns an upper-case alpha-2.
 */
export async function resolveGeoCountry(cfg: SiteRuntimeConfig, timeoutMs = 1500): Promise<string | null> {
  const url = cfg.geoLookupUrl;
  if (url === undefined || url === null || url === '') return null;
  if (typeof fetch === 'undefined') return null;
  const ctrl = typeof AbortController !== 'undefined' ? new AbortController() : undefined;
  const timer = ctrl !== undefined ? setTimeout(() => ctrl.abort(), timeoutMs) : undefined;
  try {
    const res = await fetch(url, { credentials: 'omit', cache: 'no-store', ...(ctrl !== undefined ? { signal: ctrl.signal } : {}) });
    if (!res.ok) return null;
    return pickCountry(await res.json());
  } catch {
    // offline / blocked / timeout / non-JSON — degrade to the fallback market
    return null;
  } finally {
    if (timer !== undefined) clearTimeout(timer);
  }
}

function readStore(key: string): string | undefined {
  try {
    return window.localStorage.getItem(key) ?? undefined;
  } catch {
    /* storage unavailable (private mode / blocked) — degrade to no saved value */
    return undefined;
  }
}

export function saveCountry(code: string): void {
  try {
    window.localStorage.setItem(KEY_COUNTRY, code);
  } catch {
    /* storage unavailable — the choice simply is not persisted */
  }
}

export function saveLocale(locale: string): void {
  try {
    window.localStorage.setItem(KEY_LOCALE, locale);
  } catch {
    /* storage unavailable — the choice simply is not persisted */
  }
}

/** The content for a resolved country, or undefined when the market supplies none. */
export function contentForCountry(cfg: SiteRuntimeConfig, country: string): SiteContent | undefined {
  return cfg.content?.[country.toUpperCase()];
}

/** Assemble the entry inputs from the environment (path, navigator, storage) + any fresh UI override. */
export function readEntryInputs(
  cfg: SiteRuntimeConfig,
  override?: { readonly explicitCountry?: string; readonly explicitLocale?: string },
): SiteEntryInputs {
  const nav = typeof navigator !== 'undefined' ? navigator : undefined;
  const acceptLanguage = nav?.languages !== undefined && nav.languages.length > 0 ? nav.languages.join(',') : (nav?.language ?? null);
  return {
    pathname: typeof location !== 'undefined' ? location.pathname : '/',
    geoCountryHeader: cfg.geoCountryHeader ?? null,
    savedCountry: readStore(KEY_COUNTRY),
    savedLocale: readStore(KEY_LOCALE),
    explicitCountry: override?.explicitCountry,
    explicitLocale: override?.explicitLocale,
    acceptLanguage,
    userAgent: nav?.userAgent ?? null,
    fallbackCountry: cfg.fallbackCountry,
  };
}
